import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Send, CheckCircle } from 'lucide-react';

export const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    propertyType: '',
    squareFeet: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.target;
    const data = new FormData(form);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data).toString()
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '', email: '', phone: '', address: '',
        service: '', propertyType: '', squareFeet: '', message: ''
      });
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-neutral-900 border-2 border-red-600/50 rounded-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Rhino */}
            <div className="sticky top-0 bg-red-600 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <img src="/images/logo.jpg" alt="Rhino Seal" className="w-14 h-14 rounded-full border-2 border-white" />
                <div>
                  <h2 className="text-2xl font-black text-white uppercase">Get Your Free Quote</h2>
                  <p className="text-white/80 text-sm">Fill out the form below and we'll contact you within 24 hours</p>
                </div>
              </div>
              <motion.button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-sm transition-colors"
                data-testid="quote-modal-close"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-6 h-6 text-white" />
              </motion.button>
            </div>

            {/* Content */}
            <div className="p-6">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12"
                >
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-2">Quote Request Received!</h3>
                  <p className="text-neutral-400 mb-6">
                    Thank you for your interest in Rhino Seal. We'll review your information 
                    and get back to you with a detailed quote within 24 hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.a
                      href="tel:7746967152"
                      className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase px-6 py-4 rounded-sm flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                    >
                      <Phone className="w-5 h-5" />
                      Call Now: (774) 696-7152
                    </motion.a>
                    <motion.button 
                      onClick={handleClose} 
                      className="border-2 border-gray-600 text-white px-6 py-4 rounded-sm font-bold uppercase"
                      whileHover={{ scale: 1.02, borderColor: '#dc2626' }}
                    >
                      Close
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <form
                  name="quote"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <input type="hidden" name="form-name" value="quote" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>

                  {/* Contact Info */}
                  <div>
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-sm flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-600 rounded-full" />
                      Contact Information
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                          placeholder="John Smith"
                          data-testid="quote-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                          placeholder="(555) 123-4567"
                          data-testid="quote-phone"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm text-neutral-400 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                        placeholder="john@example.com"
                        data-testid="quote-email"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm text-neutral-400 mb-2">Property Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                        placeholder="123 Main St, Worcester, MA"
                        data-testid="quote-address"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div>
                    <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-sm flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-600 rounded-full" />
                      Project Details
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Service Needed *</label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          required
                          className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                          data-testid="quote-service"
                        >
                          <option value="">Select service...</option>
                          <option value="sealcoating">Sealcoating</option>
                          <option value="crackfilling">Crack Filling</option>
                          <option value="both">Sealcoating + Crack Filling</option>
                          <option value="other">Other / Not Sure</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-neutral-400 mb-2">Property Type *</label>
                        <select
                          name="propertyType"
                          value={formData.propertyType}
                          onChange={handleChange}
                          required
                          className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                          data-testid="quote-property-type"
                        >
                          <option value="">Select type...</option>
                          <option value="residential">Residential Driveway</option>
                          <option value="commercial">Commercial Parking Lot</option>
                          <option value="hoa">HOA / Multi-Unit</option>
                          <option value="municipal">Municipal / Government</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm text-neutral-400 mb-2">
                        Estimated Square Footage (optional)
                      </label>
                      <input
                        type="text"
                        name="squareFeet"
                        value={formData.squareFeet}
                        onChange={handleChange}
                        className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm h-12 px-4"
                        placeholder="e.g., 500 sq ft"
                        data-testid="quote-sqft"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm text-neutral-400 mb-2">
                      Additional Details (optional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full bg-neutral-800 border-2 border-neutral-700 focus:border-red-600 text-white rounded-sm p-4 resize-none"
                      placeholder="Tell us about your project, any concerns, preferred timing, etc."
                      data-testid="quote-message"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-4 rounded-sm flex items-center justify-center gap-2"
                      data-testid="quote-submit"
                      whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Request Quote
                        </>
                      )}
                    </motion.button>
                    <motion.a
                      href="tel:7746967152"
                      className="border-2 border-gray-600 text-white px-6 py-4 rounded-sm flex items-center justify-center gap-2 font-bold uppercase"
                      whileHover={{ scale: 1.02, borderColor: '#dc2626' }}
                    >
                      <Phone className="w-5 h-5" />
                      Call Instead
                    </motion.a>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
